import { MajorLocation } from './../models/major-location.model';
import { Observable, throwError, timer } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from './config.service';
import { RespCenter } from '../models/resp-center.model';
import { OperatingUnit } from '../models/operating-unit.model';
import { catchError, map, mergeMap, retryWhen, shareReplay } from 'rxjs/operators';
import { AccessToken, ExpenditureType } from '../interfaces';
import { timeout } from 'rxjs/operators';

@Injectable({
  providedIn: "root"
})

export class DataService {
  ecosysData: any;
  fundingProjectMajorLocation: MajorLocation;
  private requestCache = new Map<string, { expiresAt: number; value$: Observable<any> }>();

  constructor(private config: ConfigService, private httpClient: HttpClient) {
  }

  private getOrSetCachedRequest<T>(
    cacheKey: string,
    factory: () => Observable<T>,
    ttlMs: number = 2 * 60 * 1000 // 2 Minute Time To Live
  ): Observable<T> {
    // Use a key per request shape (endpoint + params) so callers reuse the same shared stream.
    const now = Date.now();
    const cached = this.requestCache.get(cacheKey);

    // Fast path: if entry exists and has not expired, return the cached observable.
    if (cached && cached.expiresAt > now) {
      return cached.value$ as Observable<T>;
    }

    // Cache miss (or expired): create a new request stream and replay its latest value
    // to all subscribers during the TTL window.
    const request$ = factory().pipe(
      shareReplay({ bufferSize: 1, refCount: false, windowTime: ttlMs }),
      catchError((error) => {
        // Do not retain failing requests in cache; next call should retry.
        this.requestCache.delete(cacheKey);
        // rxjs ~6.4 treats a function argument as the error value itself (factory-function
        // support was only added in rxjs 7), so pass the original error object directly.
        return throwError(error);
      })
    );

    // Store the shared request stream with an absolute expiration time.
    this.requestCache.set(cacheKey, { expiresAt: now + ttlMs, value$: request$ });
    return request$;
  }

  // Retries transient failures (timeouts, network drops, 5xx) with backoff; leaves deterministic
  // failures (4xx, malformed request) alone since retrying them would just fail the same way.
  private retryWithBackoff<T>(maxRetries: number = 2, initialDelayMs: number = 1000) {
    return (source: Observable<T>): Observable<T> =>
      source.pipe(
        retryWhen((errors) =>
          errors.pipe(
            mergeMap((error, attemptIndex) => {
              const attempt = attemptIndex + 1;
              if (attempt > maxRetries || !this.isRetryableError(error)) {
                return throwError(error);
              }
              return timer(initialDelayMs * attempt);
            })
          )
        )
      );
  }

  private isRetryableError(error: any): boolean {
    if (error && error.name === 'TimeoutError') {
      return true;
    }
    const status = error && error.status;
    // status 0 typically means a network/CORS failure with no response received.
    return status === 0 || (status >= 500 && status < 600);
  }

  clearRequestCache(prefix?: string): void {
    if (!prefix) {
      this.requestCache.clear();
      return;
    }

    Array.from(this.requestCache.keys())
      .filter(key => key.includes(prefix))
      .forEach(key => this.requestCache.delete(key));
  }

  getEcosysData(): Observable<any> {
    return this.getOrSetCachedRequest(
      'aws:getEcosysData',
      () => {
        let userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
        return this.httpClient.get(this.config.getConfig('ApiBaseURLAWS').concat('/mmaps/ecosys'), { headers: { "Authorization": userInfo.idToken } });
      }
    );
  }

  getAthenaData(mmapsCallerID: string, callBody: any = null) {
    return this.getOrSetCachedRequest(
      `aws:getAthenaData:${mmapsCallerID}:${JSON.stringify(callBody)}`,
      () => {
        let userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
        return this.httpClient.post(this.config.getConfig('ApiBaseURLAWS').concat('/mmaps/athena'),
          callBody, { headers: { "Authorization": userInfo.idToken, "mmaps_caller_id": mmapsCallerID } });
      }
    );
  }

  getFireHubData(callerID: string, callBody: any = null) {
    return this.getOrSetCachedRequest(
      `aws:getFireHubData:${callerID}:${JSON.stringify(callBody)}`,
      () => {
        let userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
        return this.httpClient.post(this.config.getConfig('ApiBaseURLAWS').concat('/mmaps/fihub'),
          callBody, { headers: { "Authorization": userInfo.idToken, "fihub_db_caller_id": callerID } })
          // FiHub on-prem DB has had intermittent network/firewall connectivity issues causing
          // ~15-17s hangs per call (see fihub-prod-outage-investigation-2026-07-06). Bound the
          // client-side wait so forms with multiple allocations don't compound into 60+ second loads.
          .pipe(timeout(5000));
      }
    );
  }


  getWorkOrderTypeData(callBody: any): any {
    return this.getOrSetCachedRequest(
      `aws:getWorkOrderTypeData:${JSON.stringify(callBody)}`,
      () => {
        let userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
        return this.httpClient.post(this.config.getConfig('ApiBaseURLAWS').concat('/mmaps/work-order-type'),
          callBody, { headers: { "Authorization": userInfo.idToken } });
      }
    );
  }

  getChargeIndicatorData(callBody: any): any {
    return this.getOrSetCachedRequest(
      `aws:getChargeIndicatorData:${JSON.stringify(callBody)}`,
      () => {
        let userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
        return this.httpClient.post(this.config.getConfig('ApiBaseURLAWS').concat('/mmaps/charge-indicator'),
          callBody, { headers: { "Authorization": userInfo.idToken } });
      }
    );
  }

  getGeneratedChargingProjectID(callBody: any): any {
    return this.getOrSetCachedRequest(
      `aws:getGeneratedChargingProjectID:${JSON.stringify(callBody)}`,
      () => {
        let userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
        return this.httpClient.post(this.config.getConfig('ApiBaseURLAWS').concat('/mmaps/charging-project-id'),
          callBody, { headers: { "Authorization": userInfo.idToken } });
      }
    );
  }

  saveEstimatesData(callBody: any) {
    let userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
    this.clearRequestCache(); // Clear cache so subsequent calls will fetch new data
    return this.httpClient.post(this.config.getConfig('ApiBaseURLAWS').concat('/mmaps/estimates-cpid'),
      callBody, { headers: { "Authorization": userInfo.idToken } },);
  }

  saveCapitalRetirementsData(callBody: any) {
    let userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
    this.clearRequestCache(); // Clear cache so subsequent calls will fetch new data
    return this.httpClient.post(this.config.getConfig('ApiBaseURLAWS').concat('/mmaps/capital-retirements-cpid'),
      callBody, { headers: { "Authorization": userInfo.idToken } },);
  }

  submitProjectTEST(callBodyPassed: any): any {
    let callBody = callBodyPassed;
    let accessToken = JSON.parse(sessionStorage.getItem('currentUser')).accessToken;
    let listOfHeaders = {
      "Content-Type": "application/xml",
      "Authorization": "Bearer ".concat(accessToken),
      Accept: "*",
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "Origin, x-requested-with, Accept, Content-Type, Accept-Encoding, Accept-Language, Host, Pragma, Referrer, User-Agent, Cache-Control",
      "Access-Control-Max-Age": "3628800",
      "Access-Control-Allow-Methods": "GET, PUT, POST, DELETE, PATCH, OPTIONS"
    };
    //adding timeout to catch webmethods not getting response from powerplan 1-23-26
    //return this.httpClient.post(this.config.getConfig('ApiBaseURL').concat('/duk_fin_request_project_port/andchange_project?'),
    //  callBody, { headers: listOfHeaders, responseType: 'text' },);
    this.clearRequestCache(); // Clear cache so subsequent calls will fetch new data
    return this.httpClient.post(
      this.config.getConfig('ApiBaseURL').concat('/duk_fin_request_project_port/andchange_project?'),
      callBody,
      { headers: listOfHeaders, responseType: 'text' }
    ).pipe(
      timeout(60000) // 60 seconds
    );
  }

  getDOA(callBody: any) {
    return this.getOrSetCachedRequest(
      `api:getDOA:${JSON.stringify(callBody)}`,
      () => {
        let accessToken = JSON.parse(sessionStorage.getItem('currentUser')).accessToken;
        let listOfHeaders = {
          "Content-Type": "text/xml;charset=UTF-8",
          "Authorization": "Bearer ".concat(accessToken),
          Accept: "*",
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Headers": "Origin, x-requested-with, Accept, Content-Type, Accept-Encoding, Accept-Language, Host, Pragma, Referrer, User-Agent, Cache-Control",
          "Access-Control-Max-Age": "3628800",
          "Access-Control-Allow-Methods": "GET, PUT, POST, DELETE, PATCH, OPTIONS"
        };
        return this.httpClient.post(this.config.getConfig('ApiBaseURL').concat('/doawebservice'),
          callBody, { headers: listOfHeaders, responseType: 'text' }
        ).pipe(
          timeout(20000),
          this.retryWithBackoff(2, 1000)
        );
      }
    );
  }

  convertToEmployeeIDTEST(lanIDparam: any) {
    let lanID = lanIDparam;
    return this.getOrSetCachedRequest(
      `graph:convertToEmployeeID:${lanID}`,
      () => {
        let accessToken = JSON.parse(sessionStorage.getItem('userInfo')).accessToken;
        let listOfHeaders = {
          "ConsistencyLevel": "eventual",
          "Authorization": "Bearer ".concat(accessToken)
        };
        let url = 'https://graph.microsoft.com/v1.0/users?$count=true&$search="mailNickname:' + lanID + '"&$select=employeeId';
        return this.httpClient.get(url, { headers: listOfHeaders });
      }
    );
  }

  getUsers() {
    return this.getOrSetCachedRequest(
      'graph:getUsers',
      () => {
        let accessToken = JSON.parse(sessionStorage.getItem('userInfo')).accessToken;
        let listOfHeaders = {
          "ConsistencyLevel": "eventual",
          "Authorization": "Bearer ".concat(accessToken)
        };
        let url = 'https://graph.microsoft.com/v1.0/users';
        return this.httpClient.get(url, { headers: listOfHeaders });
      }
    );
  }

  getUsersContinued(url: string) {
    return this.getOrSetCachedRequest(
      `graph:getUsersContinued:${url}`,
      () => {
        let accessToken = JSON.parse(sessionStorage.getItem('userInfo')).accessToken;
        let listOfHeaders = {
          "ConsistencyLevel": "eventual",
          "Authorization": "Bearer ".concat(accessToken)
        };
        return this.httpClient.get(url, { headers: listOfHeaders });
      }
    );
  }

  searchUsers(name: string, filters?: { excludeContractors?: boolean }) {
    const excludeContractors = filters && filters.excludeContractors;

    return this.getOrSetCachedRequest(
      `graph:searchUsers:${name}:${excludeContractors ? 'excludeContractors' : 'all'}`,
      () => {
        let accessToken = JSON.parse(sessionStorage.getItem('userInfo')).accessToken;
        let listOfHeaders = {
          "ConsistencyLevel": "eventual",
          "Authorization": "Bearer ".concat(accessToken)
        };
        const filter = `&$filter=endswith(mail,'@duke-energy.com') ${excludeContractors ? 'and NOT(startswith(jobTitle, \'CW-\'))' : ''}`
        let url = `https://graph.microsoft.com/v1.0/users?$search="displayName:${name}"${filter}&$select=displayName,givenName,surname,mail,mailNickname,jobTitle`
        return this.httpClient.get(url, { headers: listOfHeaders });
      }
    );
  }

  getUserLanId(mail: string) {
    return this.getOrSetCachedRequest(
      `graph:getUserLanId:${mail}`,
      () => {
        let accessToken = JSON.parse(sessionStorage.getItem('userInfo')).accessToken;
        let listOfHeaders = {
          "ConsistencyLevel": "eventual",
          "Authorization": "Bearer ".concat(accessToken)
        };
        let url = `https://graph.microsoft.com/v1.0/users?$search="mail:${mail}"&$select=mailNickname`;
        return this.httpClient.get(url, { headers: listOfHeaders });
      }
    );
  }

  getGroupId(adGroup: string, {useDisplayName = false} = {}) {
    return this.getOrSetCachedRequest(
      `graph:getGroupId:${adGroup}:${useDisplayName ? 'displayName' : 'mailNickname'}`,
      () => {
        let accessToken = JSON.parse(sessionStorage.getItem('userInfo')).accessToken;
        let listOfHeaders = {
          "ConsistencyLevel": "eventual",
          "Authorization": "Bearer ".concat(accessToken)
        };
        let url = `https://graph.microsoft.com/v1.0/groups?$search="${useDisplayName ? "displayName" : "mailNickname"}:${adGroup}"`;
        return this.httpClient.get(url, { headers: listOfHeaders });
      }
    );
  }

  getGroupIdExact(adGroup: string, {useDisplayName = false} = {}) {
    return this.getGroupId(adGroup, { useDisplayName }).pipe(
      map((response: any) => {
        if (!response || !Array.isArray(response.value)) {
          return response;
        }
        const expectedValue = ((adGroup || "") as string).toLowerCase();
        const exactMatches = response.value.filter((group: any) => {
          const groupValue = useDisplayName
            ? group && group.displayName
            : group && group.mailNickname;
          return (
            typeof groupValue === "string" &&
            groupValue.toLowerCase() === expectedValue
          );
        });
        return {
          ...response,
          value: exactMatches,
        };
      })
    );
  }

  getPACUserList(groupId: string) {
    return this.getOrSetCachedRequest(
      `graph:getPACUserList:${groupId}`,
      () => {
        let accessToken = JSON.parse(sessionStorage.getItem('userInfo')).accessToken;
        let listOfHeaders = {
          "ConsistencyLevel": "eventual",
          "Authorization": "Bearer ".concat(accessToken)
        };
        let url = `https://graph.microsoft.com/v1.0/groups/${groupId}/members?$select=mailNickname,mail,displayName,givenName,surname,jobTitle`;
        return this.httpClient.get(url, { headers: listOfHeaders });
      }
    );
  }

  getPatiValidation(callBody: any) {
    return this.getOrSetCachedRequest(
      `aws:getPatiValidation:${JSON.stringify(callBody)}`,
      () => {
        let userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
        return this.httpClient.post(this.config.getConfig('ApiBaseURLAWS').concat('/mmaps/pati-pjid'),
          callBody, { headers: { "Authorization": userInfo.idToken, "pati_db_caller_id": 'check_pati_prj_id' } });
      }
    );
  }

  getRespCenters(): Observable<RespCenter[]> {
    return this.httpClient.get<RespCenter[]>('assets/resp-centers.json');
  }

  getOperatingUnits(businessUnit: string): Observable<OperatingUnit[]> {
    return this.httpClient.get<OperatingUnit[]>('assets/operating-units.json').pipe(
      map((data: OperatingUnit[]) => {
        return data.filter(unit => unit.BUSINESS_UNIT === businessUnit)
      })
    );
  }

  getExpenditureTypes(): Observable<ExpenditureType[]> {
    return this.httpClient.get<ExpenditureType[]>('assets/expenditure-type.json');
  }

  getProjectID(callBody: any): any {
    return this.getOrSetCachedRequest(
      `aws:getProjectID:${JSON.stringify(callBody)}`,
      () => {
        let userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
        return this.httpClient.post(this.config.getConfig('ApiBaseURLAWS').concat('/mmaps/itspar-guid-gen-patiprj-ddb'),
          callBody, {
            headers: {
              "Authorization": userInfo.idToken,
              "itspar_guid_apigw_caller_id": 'itspar_validate_save_pati_prj_ids',
              "Content-Type": 'application/json'
            }
          });
      })
  }

  saveItSparData(callBody: any): any {
    let userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
    this.clearRequestCache(); // Clear cache so subsequent calls will fetch new data
    return this.httpClient.post(this.config.getConfig('ApiBaseURLAWS').concat('/mmaps/itspar-review-to-ddb'),
      callBody, {
      headers: {
        "Authorization": userInfo.idToken,
        "itspar_review_to_ddb_apigw_caller_id": 'insert_itspar_review_to_ddb',
        "Content-Type": 'application/json'
      }
    },);
  }

  getItSparData(callBody: any, callerId: 'ui' | 'reviews'): any {
    const caller_ids = {
      ui: 'return_itspar_revision_to_ui',
      reviews: 'get_all_itspar_project_reviews'
    }
    return this.getOrSetCachedRequest(
      `aws:getItSparData:${callerId}:${JSON.stringify(callBody)}`,
      () => {
        let userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
        return this.httpClient.post(this.config.getConfig('ApiBaseURLAWS').concat('/mmaps/itspar-review-to-ddb'),
          callBody, {
          headers: {
            "Authorization": userInfo.idToken,
            "itspar_review_to_ddb_apigw_caller_id": caller_ids[callerId],
            "Content-Type": 'application/json'
          }
        });
      }
    ).pipe(
      // Isolate consumers from the cached response and each other.
      map(result => JSON.parse(JSON.stringify(result)))
    );
  }

  getSparGuid(): any {
    return this.getOrSetCachedRequest( 
      'aws:getSparGuid',
      () => {
        let userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
        return this.httpClient.post(this.config.getConfig('ApiBaseURLAWS').concat('/mmaps/itspar-guid-gen-patiprj-ddb'),
        '', {
          headers: {
            "Authorization": userInfo.idToken,
            "itspar_guid_apigw_caller_id": 'itspar_gen_guid',
            "Content-Type": 'application/json'
          }
        });
      })
  }

  sendEmail(callBody: any): any {
    let userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
    return this.httpClient.post(this.config.getConfig('ApiBaseURLAWS').concat('/mmaps/itspar-send-email'),
      callBody, {
      headers: {
        "Authorization": userInfo.idToken,
        "Content-Type": 'application/json'
      }
    },);
  }

  getPPLClassCodeAccessToken(): Observable<AccessToken> {
    return this.getOrSetCachedRequest(
      'aws:getPPLClassCodeAccessToken',
      () => {
        let userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
        return this.httpClient.post<AccessToken>(this.config.getConfig('ApiBaseURLAWS').concat('/mmaps/gen-pplclscode-apigw-token'), null, {
          headers: { "Authorization": userInfo.idToken }
        });
      });
  }

  getPresignedURL(callBody: any) {
    let userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
    return this.httpClient.post(this.config.getConfig('ApiBaseURLAWS').concat('/mmaps/s3-get-presigned-url'),
      callBody, {
      headers: {
        "Authorization": userInfo.idToken,
        "Content-Type": 'application/json'
      }
    },);
  }

  getPPLClassCode(token: string, callBody: any): Observable<any> {
    return this.getOrSetCachedRequest(
    `pplClassCode:${JSON.stringify(callBody)}`,
     () =>  this.httpClient.post(this.config.getConfig('PplClassCodeUrl'), callBody, { headers: { "Authorization": token }}));
    
  }

   getRREData(): Observable<any> {
    return this.getOrSetCachedRequest(
      'aws:getRREData',
      () => {
        let userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
        return this.httpClient.get(
          this.config.getConfig("ApiBaseURLAWS").concat("/mmaps/real-estate/project"),
          {
            headers: {
              Authorization: userInfo.idToken,
              "Content-Type": "application/json",
            },
          }
        );
      }
    );
  }

  getJointOwnerCoordinator(): Observable<any> {
    return this.httpClient.get('/assets/joint-owner-coordinator.json');
  }

  getLegacyFormLink(): string {
    return this.config.getConfig('legacySPARLink');
  }
}
